import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send, Loader2, ArrowUp } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import MarkdownMessage from "./MarkdownMessage";

export default function CopilotPanel({ open, onClose }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [sessionId, setSessionId] = useState(null);
    const [suggestions, setSuggestions] = useState([]);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (open && suggestions.length === 0) {
            api.get("/copilot/suggestions").then(({ data }) => setSuggestions(data.suggestions || [])).catch(() => {});
        }
    }, [open, suggestions.length]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, busy]);

    const send = async (text) => {
        const msg = (text ?? input).trim();
        if (!msg || busy) return;
        setMessages((m) => [...m, { role: "user", content: msg }]);
        setInput("");
        setBusy(true);
        try {
            const { data } = await api.post("/copilot/chat", { message: msg, session_id: sessionId, context: { screen: window.location.pathname } });
            setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "assistant", content: data.message.content }]);
        } catch (e) {
            setMessages((m) => [...m, { role: "assistant", content: "I had trouble responding. Please try again." }]);
        } finally {
            setBusy(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
            <SheetContent side="right" className="p-0 w-full sm:max-w-[440px] flex flex-col bg-card" data-testid="copilot-panel">
                <div className="border-b border-border px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <img src="/vyaahar-logo.png" alt="Vyaahar" className="h-6 object-contain dark:invert-0 invert" />
                        <div className="leading-tight">
                            <div className="text-[13px] font-semibold text-foreground">Copilot</div>
                            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-semibold">Claude Sonnet 4.5</div>
                        </div>
                    </div>
                </div>
                <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4">
                    {messages.length === 0 && (
                        <>
                            <div className="text-[13px] text-muted-foreground">
                                Ask anything about your finances. I can analyze your books, surface anomalies, and suggest actions.
                            </div>
                            <div className="space-y-1.5">
                                {suggestions.slice(0, 6).map((s) => (
                                    <button
                                        key={s}
                                        onClick={() => send(s)}
                                        className="w-full text-left text-[12.5px] px-3 py-2 rounded-md border border-border bg-muted/30 hover:bg-muted hover:border-foreground/40 transition-all"
                                        data-testid={`copilot-suggestion-${s.slice(0, 20)}`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                    {messages.map((m, i) => (
                        <div key={i} className={cn("flex", m.role === "user" ? "justify-end" : "justify-start")}>
                            <div className={cn(
                                "max-w-[85%] rounded-md px-3.5 py-2.5 text-[13px] leading-relaxed",
                                m.role === "user" ? "bg-foreground text-background whitespace-pre-wrap" : "bg-muted/60 text-foreground border border-border"
                            )}>
                                {m.role === "assistant" ? <MarkdownMessage>{m.content}</MarkdownMessage> : m.content}
                            </div>
                        </div>
                    ))}
                    {busy && (
                        <div className="flex justify-start">
                            <div className="bg-muted/60 border border-border rounded-md px-3 py-2 text-[12px] text-muted-foreground inline-flex items-center gap-2">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Thinking…
                            </div>
                        </div>
                    )}
                </div>
                <div className="border-t border-border p-4">
                    <div className="flex items-end gap-2">
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                            placeholder="Ask your finance Copilot…"
                            rows={2}
                            data-testid="copilot-input"
                            className="flex-1 resize-none bg-muted/40 border border-border rounded-md px-3 py-2 text-[13px] focus:outline-none"
                        />
                        <Button size="icon" onClick={() => send()} disabled={busy || !input.trim()} data-testid="copilot-send">
                            <ArrowUp className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}
